// src/components/AddSocialMediaModal.js
import React, { useState } from 'react';
import { X, Users, AlertCircle } from 'lucide-react';

const PLATFORMS = [
  'Facebook',
  'Instagram',
  'Twitter/X',
  'LinkedIn',
  'TikTok',
  'YouTube',
  'Telegram',
  'Discord',
  'Snapchat',
  'WhatsApp',
  'Reddit',
  'Other'
];

const ACCOUNT_TYPES = [
  'Impersonation',
  'Brand Abuse',
  'Phishing',
  'Scam',
  'Fake Customer Support',
  'Fake Executive',
  'Fake Giveaway',
  'Malware Distribution',
  'Credential Harvesting',
  'Misinformation Campaign'
];

const STATUS_OPTIONS = [
  'Active',
  'Monitoring',
  'Takedown Requested',
  'Takedown Successful',
  'Resolved',
  'False Positive'
];

const RISK_LEVELS = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Informational'
];

const AddSocialMediaModal = ({ isOpen, onClose, onAccountAdded, companies }) => {
  const [formData, setFormData] = useState({
    companyId: '',
    platform: 'Facebook',
    account_name: '',
    handle: '',
    profile_url: '',
    type: 'Impersonation',
    status: 'Active',
    risk: 'Medium',
    followers: '',
    following: '',
    posts_count: '',
    verified: false,
    discovered_date: '',
    account_created_date: '',
    content_type: '',
    impersonated_entity: '',
    screenshot_url: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setFormData({
      companyId: '',
      platform: 'Facebook',
      account_name: '',
      handle: '',
      profile_url: '',
      type: 'Impersonation',
      status: 'Active',
      risk: 'Medium',
      followers: '',
      following: '',
      posts_count: '',
      verified: false,
      discovered_date: '',
      account_created_date: '',
      content_type: '',
      impersonated_entity: '',
      screenshot_url: '',
      notes: ''
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await window.Clerk?.session?.getToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (!formData.companyId || !formData.account_name) {
        throw new Error('Company and account name are required');
      }

      // Find the company slug from numeric_id
      const selectedCompany = companies.find(c => c.numeric_id === parseInt(formData.companyId));
      if (!selectedCompany) {
        throw new Error('Invalid company selected');
      }

      const response = await fetch(`/api/companies/${selectedCompany.id}/social-media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          platform: formData.platform,
          account_name: formData.account_name,
          handle: formData.handle || null,
          profile_url: formData.profile_url || null,
          type: formData.type,
          status: formData.status,
          risk: formData.risk,
          followers: formData.followers ? parseInt(formData.followers) : null,
          following: formData.following ? parseInt(formData.following) : null,
          posts_count: formData.posts_count ? parseInt(formData.posts_count) : null,
          verified: formData.verified,
          discovered_date: formData.discovered_date || null,
          account_created_date: formData.account_created_date || null,
          content_type: formData.content_type || null,
          impersonated_entity: formData.impersonated_entity || null,
          screenshot_url: formData.screenshot_url || null,
          notes: formData.notes || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create social media account');
      }

      console.log('Social media account created:', data);
      
      if (onAccountAdded) {
        onAccountAdded(data);
      }

      resetForm();
      onClose();
      
      alert(`✅ Social media account "${data.account_name}" (${data.account_id}) created successfully!`);

    } catch (err) {
      console.error('Error creating social media account:', err);
      setError(err.message || 'Failed to create social media account');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Add Social Media Account
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Register a new impersonating/malicious social media account
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
              disabled={loading}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* Company Selection */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Company Assignment
              </h3>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  Company <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                  required
                  disabled={loading}
                >
                  <option value="">Select a company</option>
                  {companies?.map((company) => (
                    <option key={company.numeric_id} value={company.numeric_id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Account Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Account Information
              </h3>
              
              <div className="space-y-4">
                
                {/* Platform & Account Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Platform <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={formData.platform}
                      onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {PLATFORMS.map((platform) => (
                        <option key={platform} value={platform}>{platform}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Account Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Glare Industries Official"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Handle & Profile URL */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Handle
                    </label>
                    <input
                      type="text"
                      value={formData.handle}
                      onChange={(e) => setFormData({ ...formData, handle: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                      placeholder="@username"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Profile URL
                    </label>
                    <input
                      type="url"
                      value={formData.profile_url}
                      onChange={(e) => setFormData({ ...formData, profile_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="https://..."
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Type, Status, Risk */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {ACCOUNT_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Risk Level
                    </label>
                    <select
                      value={formData.risk}
                      onChange={(e) => setFormData({ ...formData, risk: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {RISK_LEVELS.map((risk) => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>
            </div>

            {/* Metrics */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Account Metrics
              </h3>
              
              <div className="space-y-4">
                
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Followers
                    </label>
                    <input
                      type="number"
                      value={formData.followers}
                      onChange={(e) => setFormData({ ...formData, followers: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="0"
                      min="0"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Following
                    </label>
                    <input
                      type="number"
                      value={formData.following}
                      onChange={(e) => setFormData({ ...formData, following: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="0"
                      min="0"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Posts
                    </label>
                    <input
                      type="number"
                      value={formData.posts_count}
                      onChange={(e) => setFormData({ ...formData, posts_count: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="0"
                      min="0"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Verified
                    </label>
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-800/70 transition-all">
                      <input
                        type="checkbox"
                        checked={formData.verified}
                        onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
                        className="w-4 h-4 rounded bg-slate-700 border-slate-600"
                        disabled={loading}
                      />
                      <span className="text-sm text-slate-300">Verified</span>
                    </label>
                  </div>
                </div>

              </div>
            </div>

            {/* Dates */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Dates
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Discovered Date
                  </label>
                  <input
                    type="date"
                    value={formData.discovered_date}
                    onChange={(e) => setFormData({ ...formData, discovered_date: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Account Created Date
                  </label>
                  <input
                    type="date"
                    value={formData.account_created_date}
                    onChange={(e) => setFormData({ ...formData, account_created_date: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Content Details */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Content Details
              </h3>
              
              <div className="space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Content Type
                    </label>
                    <input
                      type="text"
                      value={formData.content_type}
                      onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Phishing Links, Fake Giveaways"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Impersonated Entity
                    </label>
                    <input
                      type="text"
                      value={formData.impersonated_entity}
                      onChange={(e) => setFormData({ ...formData, impersonated_entity: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., CEO, Support Team, Company"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Screenshot URL
                  </label>
                  <input
                    type="url"
                    value={formData.screenshot_url}
                    onChange={(e) => setFormData({ ...formData, screenshot_url: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="https://..."
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="Additional details about this account..."
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.companyId || !formData.account_name}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddSocialMediaModal;